import { useState, useMemo, useRef, useCallback } from 'react';
import { LuChevronsLeft, LuChevronsRight } from 'react-icons/lu';
import Filter from './Filter';
import OpportunityMap, { OpportunityMapHandle } from './OpportunityMap';
import MapConfigPanel from './MapConfigPanel';
import { useConfigurationStore } from '../../stores';
import { useRegionView, RegionFilters } from './useRegionView';
import { calculateStoreRecommendations } from '../../utils/recommendationLogic';
import './OpportunitiesByRegion.css';

function OpportunitiesByRegion() {
  const [filterExpanded, setFilterExpanded] = useState(true);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const mapRef = useRef<OpportunityMapHandle>(null);
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
  const { purchek, cartManager, containment, subscription, pairedWheel } = useConfigurationStore();

  const [regionFilters, setRegionFilters] = useState<RegionFilters>({ channels: [], customerType: 'All' });
  const [recommendedSolutionsFilter, setRecommendedSolutionsFilter] = useState<string[]>([]);
  const [showNeighbourhood, setShowNeighbourhood] = useState(false);
  const [neighbourhoodRetailers, setNeighbourhoodRetailers] = useState<string[]>([]);
  const [neighbourhoodChannels, setNeighbourhoodChannels] = useState<string[]>([]);
  const [neighbourhoodStatus, setNeighbourhoodStatus] = useState<'All' | 'Customers' | 'Non-Customers'>('All');
  const [neighbourhoodSolutions, setNeighbourhoodSolutions] = useState<string[]>([]);
  const [neighbourhoodWhitespace, setNeighbourhoodWhitespace] = useState<string[]>([]);
  const [neighbourhoodAnonymous, setNeighbourhoodAnonymous] = useState(false);
  const [hideClustering, setHideClustering] = useState(false);
  const [markerSize, setMarkerSize] = useState(100);
  const regionViewEnabled = true;

  const { stateAggregates, viewportStores, currentZoomTier, isLoading } = useRegionView(
    mapInstance,
    regionFilters,
    regionViewEnabled
  );

  const handleMapReady = useCallback((map: google.maps.Map) => {
    setMapInstance(map);
  }, []);

  const purchekActiveReadinessId = purchek.activeReadiness[0];
  const purchekActiveReadinessLevel = purchek.readinessLevels.find((level: any) => level.id === purchekActiveReadinessId);
  const purchekActiveConditions = useMemo(() => purchekActiveReadinessLevel ? purchek.conditions.reduce((acc: any, cond: any) => {
    acc[cond.metric] = (cond as any)[purchekActiveReadinessId];
    return acc;
  }, {}) : {}, [purchekActiveReadinessLevel, purchekActiveReadinessId, purchek.conditions]);

  const cartManagerActiveReadinessId = cartManager.activeReadiness[0];
  const cartManagerActiveReadinessLevel = cartManager.readinessLevels.find((level: any) => level.id === cartManagerActiveReadinessId);
  const cartManagerActiveConditions = useMemo(() => cartManagerActiveReadinessLevel ? cartManager.conditions.reduce((acc: any, cond: any) => {
    acc[cond.metric] = (cond as any)[cartManagerActiveReadinessId];
    return acc;
  }, {}) : {}, [cartManagerActiveReadinessLevel, cartManagerActiveReadinessId, cartManager.conditions]);

  const containmentActiveReadinessId = containment.activeReadiness[0];
  const containmentActiveReadinessLevel = containment.readinessLevels.find((level: any) => level.id === containmentActiveReadinessId);
  const containmentActiveConditions = useMemo(() => containmentActiveReadinessLevel ? containment.conditions.reduce((acc: any, cond: any) => {
    acc[cond.metric] = (cond as any)[containmentActiveReadinessId];
    return acc;
  }, {}) : {}, [containmentActiveReadinessLevel, containmentActiveReadinessId, containment.conditions]);

  const subscriptionActiveReadinessId = subscription.activeReadiness[0];
  const subscriptionActiveReadinessLevel = subscription.readinessLevels.find((level: any) => level.id === subscriptionActiveReadinessId);
  const subscriptionActiveConditions = useMemo(() => subscriptionActiveReadinessLevel ? subscription.conditions.reduce((acc: any, cond: any) => {
    acc[cond.metric] = (cond as any)[subscriptionActiveReadinessId];
    return acc;
  }, {}) : {}, [subscriptionActiveReadinessLevel, subscriptionActiveReadinessId, subscription.conditions]);

  const pairedWheelActiveReadinessId = pairedWheel.activeReadiness[0];
  const pairedWheelActiveReadinessLevel = pairedWheel.readinessLevels.find((level: any) => level.id === pairedWheelActiveReadinessId);
  const pairedWheelActiveConditions = useMemo(() => pairedWheelActiveReadinessLevel ? pairedWheel.conditions.reduce((acc: any, cond: any) => {
    acc[cond.metric] = (cond as any)[pairedWheelActiveReadinessId];
    return acc;
  }, {}) : {}, [pairedWheelActiveReadinessLevel, pairedWheelActiveReadinessId, pairedWheel.conditions]);

  // Filter viewport stores based on viewing mode
  const filteredViewportStores = useMemo(() => {
    const mode = regionFilters.viewingMode || 'all';
    if (mode === 'all') return viewportStores;

    if (mode === 'whitespace') {
      return viewportStores.filter((store) =>
        !store.purchek_install_date ||
        !store.containment_install_date ||
        !store.cart_manager_install_date ||
        !store.subscription_start_date ||
        !store.paired_wheel_install_date
      );
    }

    if (mode === 'recommended') {
      return viewportStores.filter((store) => {
        const recs = calculateStoreRecommendations(
          store,
          (store as any).estimated_no_of_carts_per_store || 0,
          purchekActiveConditions,
          cartManagerActiveConditions,
          containmentActiveConditions,
          subscriptionActiveConditions,
          pairedWheelActiveConditions,
          purchekActiveReadinessLevel?.name,
          cartManagerActiveReadinessLevel?.name,
          containmentActiveReadinessLevel?.name,
          subscriptionActiveReadinessLevel?.name,
          pairedWheelActiveReadinessLevel?.name,
        );

        const hasAnyRec =
          recs.purchek.length > 0 ||
          recs.containment.length > 0 ||
          recs.cartManager.length > 0 ||
          recs.subscription.length > 0 ||
          recs.pairedWheel.length > 0 ||
          recs.wheelReorder.length > 0 ||
          recs.pendingService.length > 0;

        if (!hasAnyRec) return false;

        // If specific recommended solutions are selected, filter to only those
        if (recommendedSolutionsFilter.length > 0) {
          return recommendedSolutionsFilter.some((sol) => {
            switch (sol) {
              case 'Purchek': return recs.purchek.length > 0;
              case 'Cart Containment': return recs.containment.length > 0;
              case 'Cart Manager': return recs.cartManager.length > 0;
              case 'Subscription': return recs.subscription.length > 0;
              case 'Paired Wheel': return recs.pairedWheel.length > 0;
              case 'Wheel Reorder': return recs.wheelReorder.length > 0;
              case 'Pending Service': return recs.pendingService.length > 0;
              default: return false;
            }
          });
        }

        return true;
      });
    }

    return viewportStores;
  }, [viewportStores, regionFilters.viewingMode, recommendedSolutionsFilter, purchekActiveConditions, cartManagerActiveConditions, containmentActiveConditions, subscriptionActiveConditions, pairedWheelActiveConditions, purchekActiveReadinessLevel?.name, cartManagerActiveReadinessLevel?.name, containmentActiveReadinessLevel?.name, subscriptionActiveReadinessLevel?.name, pairedWheelActiveReadinessLevel?.name]);

  return (
    <div className="opportunities-by-region-container">
      {isLoading && <div className="top-loading-bar" />}
      <div className="opportunities-by-region-layout">
        <div className="sidebars-container">
          {/* Filter Sidebar */}
          <aside className={`map-builder-sidebar ${filterExpanded ? 'expanded' : 'collapsed'}`}>
            {/* Collapsed State - Only Show Tab */}
            {!filterExpanded && (
              <button
                className="builder-collapse-tab"
                onClick={() => setFilterExpanded(true)}
                title="Expand Filter"
              >
                <LuChevronsRight size={16} />
                <span className="builder-tab-label">Filter</span>
              </button>
            )}

            {/* Expanded State - Show Full Content */}
            {filterExpanded && (
              <div className="map-builder-wrapper">
                <div className="builder-header-with-close">
                  <h3 className="builder-title">Filter</h3>
                  <button
                    className="builder-collapse-btn"
                    onClick={() => setFilterExpanded(false)}
                    title="Collapse"
                  >
                    <LuChevronsLeft size={18} />
                  </button>
                </div>
                <div className="map-builder-content-wrapper">
                  <Filter
                    onFilterChange={(filters) => {
                      setShowNeighbourhood(!!filters.showNeighbourhood);
                      setRecommendedSolutionsFilter(filters.recommendedSolutions || []);
                      setNeighbourhoodRetailers(filters.neighbourhoodRetailers || []);
                      setNeighbourhoodChannels(filters.neighbourhoodChannels || []);
                      setNeighbourhoodStatus((filters.neighbourhoodStatus as 'All' | 'Customers' | 'Non-Customers') || 'All');
                      setNeighbourhoodSolutions(filters.neighbourhoodSolutions || []);
                      setNeighbourhoodWhitespace(filters.neighbourhoodWhitespace || []);
                      setNeighbourhoodAnonymous(!!filters.neighbourhoodAnonymous);
                      setRegionFilters({
                        channels: [],
                        customerType: 'All',
                        states: filters.states,
                        cities: filters.cities,
                        zipcodes: filters.zipcodes,
                        retailers: filters.retailers,
                        brands: filters.brands,
                        categories: filters.categories,
                        solutions: filters.solutions,
                        whitespace: filters.whitespace,
                        viewingMode: filters.viewingMode,
                        penetrationMin: filters.penetrationMin,
                        penetrationMax: filters.penetrationMax,
                        crimeIndex: filters.crimeIndex,
                        weatherHotMonthsMin: filters.weatherHotMonthsMin,
                        weatherHotMonthsMax: filters.weatherHotMonthsMax,
                        weatherColdMonthsMin: filters.weatherColdMonthsMin,
                        weatherColdMonthsMax: filters.weatherColdMonthsMax,
                        shoppingComplexOnly: filters.shoppingComplexOnly,
                        cartOrdinance: filters.cartOrdinance,
                      });
                    }}
                  />
                </div>
              </div>
            )}
          </aside>
        </div>

        <main className="opportunity-map-main">
          <MapConfigPanel
            showHeatmap={showHeatmap}
            onHeatmapChange={setShowHeatmap}
            neighbourhoodAnonymous={neighbourhoodAnonymous}
            onNeighbourhoodAnonymousChange={setNeighbourhoodAnonymous}
            hideClustering={hideClustering}
            onHideClusteringChange={setHideClustering}
            markerSize={markerSize}
            onMarkerSizeChange={setMarkerSize}
          />
          <OpportunityMap
            ref={mapRef}
            hasBrandLayers={false}
            layerStores={{}}
            layersWithStores={[]}
            showHeatmap={showHeatmap}
            regionViewEnabled={regionViewEnabled}
            stateAggregates={stateAggregates}
            regionViewStores={filteredViewportStores}
            regionZoomTier={currentZoomTier}
            onMapReady={handleMapReady}
            showNeighbourhood={showNeighbourhood}
            neighbourhoodRetailers={neighbourhoodRetailers}
            neighbourhoodChannels={neighbourhoodChannels}
            neighbourhoodStatus={neighbourhoodStatus}
            neighbourhoodSolutions={neighbourhoodSolutions}
            neighbourhoodWhitespace={neighbourhoodWhitespace}
            neighbourhoodAnonymous={neighbourhoodAnonymous}
            hideClustering={hideClustering}
            markerSize={markerSize}
            regionLoading={isLoading}
            recommendationConfigs={{
              purchekConditions: purchekActiveConditions,
              cartManagerConditions: cartManagerActiveConditions,
              containmentConditions: containmentActiveConditions,
              subscriptionConditions: subscriptionActiveConditions,
              pairedWheelConditions: pairedWheelActiveConditions,
              purchekReadinessName: purchekActiveReadinessLevel?.name,
              cartManagerReadinessName: cartManagerActiveReadinessLevel?.name,
              containmentReadinessName: containmentActiveReadinessLevel?.name,
              subscriptionReadinessName: subscriptionActiveReadinessLevel?.name,
              pairedWheelReadinessName: pairedWheelActiveReadinessLevel?.name,
            }}
          />
        </main>
      </div>
    </div>
  );
}

export default OpportunitiesByRegion;
